import { getCore } from 'cvat-core-wrapper';

export type StepKind = 'align' | 'lambda' | 'library';
export type StepStatus = 'unavailable' | 'disabled' | 'running' | 'queued' | 'failed' | 'done' | 'partial' | 'pending';
export type FrameStatus = 'running' | 'failed' | 'done' | 'partial' | 'pending';

export interface StepCandidate {
    id: string;
    name: string;
    recommended: boolean;
    deployed: boolean | null;
}

export interface StepCounts {
    done: number;
    failed: number;
    running: number;
    queued: number;
    total: number;
}

export interface StepFailure {
    frame: number;
    error: string;
    error_detail: string | null;
    // when it failed: an old failure may come from an older deployment
    at: string;
}

export interface StepState {
    id: string;
    title: string;
    kind: StepKind;
    optional: boolean;
    requires: string[];
    enabled: boolean;
    available: boolean;
    reason: string | null;
    function: { id: string; name: string } | null;
    candidates: StepCandidate[];
    counts: StepCounts;
    status: StepStatus;
    // short and readable; the technical text, when there is one, is in error_detail
    error: string | null;
    error_detail?: string | null;
    done_frames?: number[];
    running_frames?: number[];
    queued_frames?: number[];
    // newest first
    failures?: StepFailure[];
}

export type StepsScope = 'drawings' | 'references';

// a photo as the server names it in a run request
export interface PhotoKey {
    reference: number;
    frame: number;
}

export interface StepsState {
    // what Find furniture in library searches; empty means the references (drawings) or the whole share (photos)
    libraries?: string[];
    // references scope: frames are positions in this list
    photos?: { reference: number; job: number; frame: number }[];
    frames: number[];
    running: boolean;
    results_version: string | null;
    steps: StepState[];
    frame_status: Record<string, FrameStatus>;
    // drawings: their images were replaced by aligned ones, the originals can be put back
    restorable?: boolean;
}

export interface RunRequest {
    steps: string[];
    frames: number[] | null;
    force?: boolean;
}

// what the server takes: no list means all of them
export interface RunBody {
    steps: string[];
    frames?: number[];
    photos?: PhotoKey[];
    force?: boolean;
}

export interface StepSettings {
    enabled?: Record<string, boolean | null>;
    functions?: Record<string, string | null>;
    libraries?: string[];
}

export interface ProjectStepInfo {
    id: string;
    title: string;
    kind: StepKind;
    optional: boolean;
    enabled_by_default: boolean;
    unavailable_reason: string;
    candidates: StepCandidate[];
}

export interface ProjectStepDefaults {
    settings: StepSettings;
    steps: ProjectStepInfo[];
}

export interface LibraryOption {
    key: string;
    value: string;
}

export interface RunResult {
    state: StepsState;
    // false when every unit was already done: the server answers 200 instead of 202
    queued: boolean;
}

async function request(path: string, method: string, data?: unknown): Promise<{ data: unknown; status: number }> {
    const core = getCore();
    return core.server.request(`${core.config.backendAPI}${path}`, {
        method,
        ...(data === undefined ? {} : { data }),
    });
}

async function call<T>(path: string, method: string, data?: unknown): Promise<T> {
    const response = await request(path, method, data);
    return response.data as T;
}

const stepsPath = (taskId: number, scope: StepsScope): string => (
    `/tasks/${taskId}/${scope === 'references' ? 'reference_steps' : 'steps'}`
);

export function fetchSteps(taskId: number, scope: StepsScope = 'drawings'): Promise<StepsState> {
    return call<StepsState>(stepsPath(taskId, scope), 'GET');
}

export async function runSteps(
    taskId: number, run: RunBody, scope: StepsScope = 'drawings',
): Promise<RunResult> {
    const response = await request(stepsPath(taskId, scope), 'POST', run);
    return { state: response.data as StepsState, queued: response.status === 202 };
}

export function stopSteps(taskId: number, scope: StepsScope = 'drawings'): Promise<StepsState> {
    return call<StepsState>(stepsPath(taskId, scope), 'DELETE');
}

export function updateTaskStepSettings(
    taskId: number, settings: StepSettings, scope: StepsScope = 'drawings',
): Promise<StepsState> {
    return call<StepsState>(stepsPath(taskId, scope), 'PATCH', settings);
}

export function fetchProjectStepDefaults(projectId: number): Promise<ProjectStepDefaults> {
    return call<ProjectStepDefaults>(`/projects/${projectId}/step_defaults`, 'GET');
}

export function saveProjectStepDefaults(projectId: number, settings: StepSettings): Promise<ProjectStepDefaults> {
    return call<ProjectStepDefaults>(`/projects/${projectId}/step_defaults`, 'PUT', settings);
}

// with a task the list also has the libraries of its project folder
export function fetchLibraries(taskId?: number): Promise<LibraryOption[]> {
    return call<LibraryOption[]>(taskId ? `/get_library?task_id=${taskId}` : '/get_library', 'GET');
}

// replaced: its image was replaced by an aligned one before, it lies on the first drawing already
export type AlignItemStatus = 'base' | 'pending' | 'aligned' | 'manual' | 'failed' | 'replaced';

export interface AlignItem {
    frame: number;
    name: string;
    status: AlignItemStatus;
    // the full size: the light copies the review shows may be smaller
    width: number;
    height: number;
    // 2x3, from the pixels of the drawing to those of the first one
    matrix?: number[][];
    // names its aligned image: a drawing kept from the run before keeps it
    version?: string;
    error?: string;
}

export interface AlignState {
    status: 'none' | 'running' | 'done' | 'failed';
    // names the previews of a run
    run: string | null;
    error: string;
    items: AlignItem[];
}

export const alignPath = (taskId: number): string => `/tasks/${taskId}/align`;

export function fetchAlign(taskId: number): Promise<AlignState> {
    return call<AlignState>(alignPath(taskId), 'GET');
}

// manual: the drawings aligned by hand (their matrices), null aligns one automatically again
export function startAlign(taskId: number, manual: Record<number, number[][] | null> = {}): Promise<AlignState> {
    return call<AlignState>(alignPath(taskId), 'POST', { manual });
}

// run: the result the user looked at
export function applyAlign(taskId: number, run: string | null): Promise<AlignState> {
    return call<AlignState>(alignPath(taskId), 'PUT', { run });
}

export function restoreAligned(taskId: number): Promise<AlignState> {
    return call<AlignState>(`${alignPath(taskId)}/restore`, 'POST');
}

export function discardAlign(taskId: number): Promise<AlignState> {
    return call<AlignState>(alignPath(taskId), 'DELETE');
}

// the drawing as it is now: a light copy the alignment made, else the frame as the editor gets it
export function drawingSources(taskId: number, frame: number, version?: string): string[] {
    const { backendAPI } = getCore().config;
    return [
        `${backendAPI}${alignPath(taskId)}/preview?frame=${frame}&kind=drawing&version=${version}`,
        `${backendAPI}/tasks/${taskId}/data?type=frame&number=${frame}&quality=compressed`,
    ];
}

// the drawing as it would be after replacing, a light copy
export function alignedUrl(taskId: number, frame: number, version?: string): string {
    return `${getCore().config.backendAPI}${alignPath(taskId)}/preview?frame=${frame}&kind=aligned&version=${version}`;
}
